<nav id="esp-side-nav" [attr.aria-label]="'nav.main.label' | translate" [ngClass]="{'menu-collapsed': !isMenuExpanded, 'menu-expanding': isExpanding, 'keyboard-triggered': keyboardTriggered, 'in-focus': isInFocus}" (transitionend)="onNavTransitionEnd($event)">
  <!-- Lyra collapse toggle: 20px round chevron straddling the sidebar's right edge. -->
  <button type="button"
     class="toggle-menu-collapse"
     [class.collapsed]="!isMenuExpanded"
     [attr.aria-label]="'nav.sidebar.toggle' | translate"
     [attr.aria-expanded]="isMenuExpanded"
     [attr.aria-controls]="'esp-side-nav-items'"
     [pTooltip]="'nav.sidebar.toggle' | translate"
     tooltipPosition="right"
     tooltipEvent="both"
     #toggleButton
     (click)="toggleMenuCollapse()"
     (keyup)="$event.stopPropagation()">
    <svg class="toggle-icon" width="20" height="20" viewBox="0 0 20 20" fill="none"
         xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <rect class="toggle-icon-bg" width="20" height="20" rx="10"/>
      <rect class="toggle-icon-border" x="0.5" y="0.5" width="19" height="19" rx="9.5"/>
      <path class="toggle-icon-chevron" d="M9 7L12 10L9 13" stroke-linecap="round"/>
    </svg>
  </button>
  <div class="menu-items" id="esp-side-nav-items">
    <ul *ngIf="inputMenuItems" #tree role="tree" name="menu" accesskey="w" tabindex="{{tabIndex}}"
        [attr.aria-activedescendant]="ariaActiveDescendant"
        (focus)="onFocus()" (focusout)="onFocusOut()" (keydown)="onTreeKeydown($event)">
    <li *ngFor="let menuItem of inputMenuItems"
      role="treeitem"
      [id]="menuItem.id"
      aria-level="1"
      [attr.aria-selected]="(menuItem == focusedItem && focusedSubItem==null)"
      [attr.aria-current]="(menuItem == activeItem && !activeSubItem) ? 'page' : null"
      [attr.aria-expanded]="!menuItem.subItems ? null : (isMenuExpanded ? !!menuItem.expanded : flyoutItem === menuItem)"
      [attr.aria-controls]="(!isMenuExpanded && menuItem.subItems && flyoutItem === menuItem) ? flyoutId(menuItem) : null"
      [ngClass]="{'menu-item-selected': (menuItem == focusedItem && focusedSubItem==null)}"
      #itemTooltip
      [pTooltip]="menuItem.key | translate"
      tooltipPosition="right"
      tooltipEvent="focus"
      [tooltipDisabled]="isMenuExpanded">
      <ng-container *ngIf="!menuItem.subItems; else groupHeader">
        <a class="menu-item"
          (click)="handleMenuEvent(menuItem, $event)"
          (mouseenter)="onItemMouseEnter(menuItem, $event)"
          (mouseleave)="onItemMouseLeave()"
          tabindex="-1"
          [ngClass]="{'active-item': menuItem == activeItem, 'selected': menuItem == activeItem}">
          <ng-container *ngTemplateOutlet="rowContent; context: {$implicit: menuItem}"></ng-container>
        </a>
      </ng-container>
      <ng-template #groupHeader>
        <button type="button"
          class="menu-item group-header"
          (click)="handleMenuEvent(menuItem, $event)"
          (mouseenter)="onItemMouseEnter(menuItem, $event)"
          (mouseleave)="onItemMouseLeave()"
          tabindex="-1"
          [ngClass]="{'active-item': menuItem == activeItem, 'selected': menuItem == activeItem && (!isMenuExpanded || !activeSubItem)}">
          <ng-container *ngTemplateOutlet="rowContent; context: {$implicit: menuItem}"></ng-container>
          <esp-icon class="menu-item-expand" name="chevron-down" [ngClass]="{'expanded': menuItem.expanded}"></esp-icon>
        </button>
        <!-- Accordion: the grid row animates 0fr -> 1fr (height) with opacity, 200ms ease-in. -->
        <div class="sub-menu-wrapper" [class.expanded]="menuItem.expanded">
          <ul class="sub-menu" [ngClass]="{'expanded': menuItem.expanded}" role="group">
            <li *ngFor="let subItem of menuItem.subItems"
              role="treeitem"
              [id]="subItem.id"
              aria-level="2"
              [attr.aria-selected]="(subItem == focusedSubItem)"
              [attr.aria-current]="subItem == activeSubItem ? 'page' : null"
              [ngClass]="{'menu-item-selected': (subItem == focusedSubItem)}">
              <a class="sub-menu-item"
                (click)="handleSubMenuEvent(menuItem, subItem, $event)"
                tabindex="-1"
                [ngClass]="{'active-sub-item': subItem == activeSubItem, 'selected': subItem == activeSubItem}">
                <span class="menu-item-title">{{subItem.key | translate}}</span>
              </a>
            </li>
          </ul>
        </div>
      </ng-template>
    </li>
    </ul>
  </div>
</nav>

<!-- Row content shared by the sidebar rows and the collapsed flyout (icons on top-level items only). -->
<ng-template #rowContent let-item>
  <esp-icon class="menu-item-icon" *ngIf="item.icon" [name]="item.icon"></esp-icon>
  <span class="menu-item-title">{{item.key | translate}}</span>
</ng-template>

<!-- Collapsed-rail flyout, rendered in a CDK overlay to the right of the hovered/activated item. -->
<ng-template #flyoutTemplate let-item>
  <div class="side-nav-flyout"
       [id]="flyoutId(item)"
       [class.closing]="flyoutClosing"
       (mouseenter)="onFlyoutMouseEnter()"
       (mouseleave)="onFlyoutMouseLeave()"
       (keydown)="onFlyoutKeydown($event)">
    <div class="flyout-content">
      <ng-container *ngIf="item.subItems; else singleFlyout">
        <div class="flyout-group-header">
          <ng-container *ngTemplateOutlet="rowContent; context: {$implicit: item}"></ng-container>
        </div>
        <ul class="flyout-sub-items" [attr.aria-label]="item.key | translate">
          <li *ngFor="let subItem of item.subItems">
            <a class="flyout-row flyout-sub-row" tabindex="0" role="link"
               [class.selected]="subItem == activeSubItem"
               [attr.aria-current]="subItem == activeSubItem ? 'page' : null"
               (click)="onFlyoutSubItemActivate(item, subItem, $event)"
               (keydown.enter)="onFlyoutSubItemActivate(item, subItem, $event)"
               (keydown.space)="onFlyoutSubItemActivate(item, subItem, $event)">
              <span class="menu-item-title">{{subItem.key | translate}}</span>
            </a>
          </li>
        </ul>
      </ng-container>
      <ng-template #singleFlyout>
        <a class="flyout-row" tabindex="0" role="link"
           [class.selected]="item == activeItem"
           [attr.aria-current]="item == activeItem ? 'page' : null"
           (click)="onFlyoutItemActivate(item, $event)"
           (keydown.enter)="onFlyoutItemActivate(item, $event)"
           (keydown.space)="onFlyoutItemActivate(item, $event)">
          <ng-container *ngTemplateOutlet="rowContent; context: {$implicit: item}"></ng-container>
        </a>
      </ng-template>
    </div>
  </div>
</ng-template>
